import React from 'react';
import clsx from 'clsx';
import {
  Form,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSubmit,
  useActionData,
  useOutlet,
  useLocation
} from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';

import * as Headless from '@headlessui/react';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CubeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { PageTitle } from '@oysterjs/uiv2/page';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { Input, InputGroup } from '@oysterjs/uiv2/input';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/uiv2/fieldset';

import { client } from '../../api/client';
import {
  CreateCaraWorkspaceDocument,
  CreateCaraWorkspaceMutation,
  CreateCustomPresetMutation,
  CustomPresetFieldsFragment,
  CustomPresetInput,
  UpdateCustomPresetMutation
} from '../../api/graphql';
import { ApolloError } from '@apollo/client';
import { Await, TransitionDiv } from '@oysterjs/uiv2/async';
import { Button } from '@oysterjs/uiv2/button';
import {
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
  MountableDialog
} from '@oysterjs/uiv2/dialog';
import { AutoResizeTextarea, Textarea } from '@oysterjs/uiv2/textarea';
import { FileInput } from '@oysterjs/uiv2/file';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/uiv2/checkbox';

export const PresetsPage = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | { data: Promise<{ presets?: CustomPresetFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Presets" description="Create your own templates for Copilot workspaces">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between gap-4 flex-col xs:flex-row">
            <form className="w-full max-w-md">
              <Fieldset>
                <FieldGroup>
                  <div className="flex gap-4">
                    <Field className="w-full max-w-md">
                      <Label className="sr-only" data-slot="none">
                        Filter table
                      </Label>
                      <InputGroup>
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        <Input
                          type="search"
                          name="search"
                          placeholder="Start typing to filter..."
                        />
                      </InputGroup>
                    </Field>
                  </div>
                </FieldGroup>
              </Fieldset>
            </form>
            <Button color="sky" href="/app/presets/new" className="flex-none">
              Create Preset
            </Button>
          </div>
        </div>

        <React.Suspense fallback={<PresetsPageLoader />}>
          <Await resolve={loader?.data}>
            {({ presets }) => (
              <div className="px-4 sm:px-6 rounded-lg bg-white shadow">
                {!presets?.length && (
                  <div className="text-center py-12">
                    <CubeIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                      No presets found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">
                      Create a copilot workspace to get started.
                    </p>
                  </div>
                )}
                {!!presets?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Title</TableHeader>
                        <TableHeader className="py-4">Created By</TableHeader>
                        <TableHeader className="py-4">Last Updated</TableHeader>
                        <TableHeader className="py-4">
                          <div className="sr-only">Actions</div>
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {presets.map((preset) => (
                        <TableRow key={preset.id}>
                          <TableCell>{preset.title}</TableCell>
                          <TableCell>{preset.createdBy.name}</TableCell>
                          <TableCell>
                            {new Date(preset.updatedAt).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-row gap-4">
                              <Headless.Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/app/presets/${preset.id}/run`);
                                }}
                                className="font-medium text-primary-600 hover:text-primary-900"
                              >
                                Run
                              </Headless.Button>
                              <Headless.Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/app/presets/${preset.id}/edit`);
                                }}
                                className="font-medium text-primary-600 hover:text-primary-900"
                              >
                                Edit
                              </Headless.Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </PageTitle>
  );
};

export const PresetsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading presets...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

const tools = [
  {
    name: 'web_research',
    description: 'Cara can search the web for specific information and use it to complete the task.'
  },
  {
    name: 'draft_email',
    description: 'Cara can draft an email to a particular individual.'
  },
  {
    name: 'search_file',
    description: 'Cara can search an uploaded file for information and use it to complete the task.'
  },
  {
    name: 'generate_document',
    description: 'Cara can generate a PDF document based on the provided information.'
  }
];

export const PresetCreatePage = () => {
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateCustomPresetMutation>;
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  return (
    <PresetsCreateOrEditPage
      editMode={false}
      initialForm={{
        title: '',
        basePrompt: '',
        allowFileUpload: true,
        description: ''
      }}
      onSubmit={(form) => submit(form, { method: 'POST', encType: 'application/json' })}
      validationErrors={validationErrors}
    />
  );
};

export const PresetEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ preset?: CustomPresetFieldsFragment }> }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => {
          if (!data?.preset) {
            return null;
          }

          const submit = useSubmit();
          const action = useActionData() as ActionResult<UpdateCustomPresetMutation>;
          const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

          React.useEffect(() => {
            if (action?.validationErrors) {
              setValidationErrors(action.validationErrors);
            }
          }, [action?.validationErrors]);

          return (
            <PresetsCreateOrEditPage
              editMode
              initialForm={{
                title: data.preset.title || '',
                basePrompt: data.preset.basePrompt || '',
                allowFileUpload: data.preset.allowFileUpload || true,
                description: data.preset.description || ''
              }}
              onSubmit={(form) => submit(form, { method: 'PUT', encType: 'application/json' })}
              validationErrors={validationErrors}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export const PresetsCreateOrEditPage = (props: {
  editMode?: boolean;
  initialForm: CustomPresetInput;
  onSubmit: (form: CustomPresetInput) => void;
  validationErrors: ValidationError[];
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<CustomPresetInput>(props.initialForm);
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.onSubmit(form);
  };

  const [showHelp, setShowHelp] = React.useState(false);

  return (
    <MountableDialog
      onClose={() => navigate(-1)}
      size={showHelp ? '5xl' : '2xl'}
      className="relative"
    >
      <Headless.Button
        onClick={() => setShowHelp((prev) => !prev)}
        className={clsx(
          'z-50 absolute top-12 text-neutral-600 bg-neutral-200 hover:bg-neutral-600 hover:text-neutral-100 transition-all duration-150 flex flex-row gap-1 items-center rounded-full py-1 px-2',
          showHelp ? 'right-12' : 'right-8'
        )}
      >
        {showHelp ? (
          <ChevronDoubleRightIcon className="size-3" />
        ) : (
          <ChevronDoubleLeftIcon className="size-3" />
        )}
        {showHelp ? (
          <div className="text-xs">Hide help</div>
        ) : (
          <div className="text-xs">Show help</div>
        )}
      </Headless.Button>
      <div className="flex flex-row gap-8">
        <div className="max-w-[608px]">
          <DialogTitle>{props.editMode ? 'Edit preset' : 'Create a new preset'}</DialogTitle>
          <DialogDescription>
            {props.editMode
              ? 'Update the preset settings and save your changes.'
              : 'Define a new preset and start creating copilot workspaces with it.'}
          </DialogDescription>
          <Form onSubmit={handleSubmit}>
            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label>Title</Label>
                    <Description>
                      A brief, descriptive title for the preset. This should be two to four words.
                    </Description>
                    <Input
                      className="max-w-xs"
                      value={form?.title}
                      onChange={(e) => {
                        const title = e.currentTarget.value;
                        updateForm(['title'], (prev) => ({ ...prev, title }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['title'])}
                    />
                    {!!getValidationError(validationErrors, ['title']) && (
                      <ErrorMessage>{getValidationError(validationErrors, ['title'])}</ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Description</Label>
                    <Description>
                      A brief description of the preset does. This should be about one sentence.
                    </Description>
                    <Textarea
                      value={form?.description}
                      rows={2}
                      resizable={false}
                      onChange={(e) => {
                        const description = e.currentTarget.value;
                        updateForm(['description'], (prev) => ({ ...prev, description }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['description'])}
                    />
                    {!!getValidationError(validationErrors, ['description']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['description'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Base Prompt</Label>
                    <Description>
                      Specify detailed instructions for what you want Cara to do. Read the{' '}
                      <a
                        onClick={() => setShowHelp(true)}
                        className="text-sky-600 hover:text-sky-900 hover:underline cursor-pointer"
                      >
                        help
                      </a>{' '}
                      section for tips and guidelines on how to prompt Cara.
                    </Description>
                    <AutoResizeTextarea
                      className="font-mono"
                      value={form?.basePrompt}
                      onChange={(e) => {
                        const basePrompt = e.currentTarget.value;
                        updateForm(['basePrompt'], (prev) => ({ ...prev, basePrompt }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['basePrompt'])}
                    />
                    {!!getValidationError(validationErrors, ['basePrompt']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['basePrompt'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </FieldGroup>
                <CheckboxGroup>
                  <CheckboxField>
                    <Checkbox
                      color="sky"
                      checked={form?.allowFileUpload}
                      onChange={(allowFileUpload) => {
                        updateForm(['allowFileUpload'], (prev) => ({
                          ...prev,
                          allowFileUpload
                        }));
                      }}
                    />
                    <Label>Allow file upload</Label>
                    <Description>
                      Allow users to upload one or more files when using this preset. Cara will be
                      able to reference those files in the workspace to complete the task.
                    </Description>
                  </CheckboxField>
                </CheckboxGroup>
              </Fieldset>
            </DialogBody>
            <DialogActions>
              <Button
                type="button"
                color="white"
                disabled={navigation.state === 'submitting'}
                onClick={() => navigate(-1)}
              >
                Close
              </Button>
              <Button
                type="submit"
                color="sky"
                disabled={!form}
                loading={navigation.state === 'submitting'}
              >
                {props.editMode ? 'Update Preset' : 'Create Preset'}
              </Button>
            </DialogActions>
          </Form>
        </div>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: '320px' }}
            transition={{ delay: 0.15, duration: 0.15, ease: 'easeOut' }}
            className="flex-none bg-neutral-50 rounded-lg p-6 overflow-y-auto max-h-[733.5px]"
          >
            <motion.div
              initial={{ opacity: 0, display: 'none' }}
              animate={{ opacity: 1, display: 'block' }}
              transition={{ delay: 0.3, duration: 0.15 }}
            >
              <h2 className="text-lg font-medium text-neutral-900 mt-0 pt-0">Help</h2>
              <p className="text-sm text-neutral-500">
                Cara is an insurance-specific AI that you can program to perform custom tasks.
                Here's an overview of how to effectively use Cara.
              </p>
              <h3 className="text-sm font-medium text-neutral-900 mt-4">Prompting Cara</h3>
              <p className="text-sm text-neutral-500">
                Cara's performance depends on the quality of your prompt. You'll get the best
                results if you follow these guidelines:
                <ul className="mt-3 -ml-7">
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 font-medium">Treat it like a human.</strong>{' '}
                    Cara is smart, but does not know your specific use case. Train it like you would
                    a new employee.
                  </li>
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 font-medium">
                      Be detailed and specific.
                    </strong>{' '}
                    Cara is capable, but not a mind-reader. Tell it exactly what you want it to do.
                  </li>
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 font-medium">Provide context.</strong>{' '}
                    Include context, background, examples, and any key facts and information that
                    can help Cara reason about the task.
                  </li>
                  <li className="mt-1.5">
                    <strong className="text-neutral-900 font-medium">
                      Specify the output format.
                    </strong>{' '}
                    If you want Cara to respond in a particular format, instruct her explicitly.
                  </li>
                </ul>
              </p>
              <h3 className="text-sm font-medium text-neutral-900 mt-4">Tools</h3>
              <p className="text-sm text-neutral-500">
                Cara has access to a variety of tools that can help it complete your request. You
                can tell it explicitly to use these or instruct it on how to decide whether to use
                them.
              </p>
              <ul className="mt-3">
                {tools.map((tool) => (
                  <li className="mt-1.5" key={tool.name}>
                    <span className="text-sm font-medium font-mono text-neutral-900">
                      {tool.name}
                    </span>

                    <p className="text-sm text-neutral-500">{tool.description}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </div>
    </MountableDialog>
  );
};

export const PresetRunPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ preset?: CustomPresetFieldsFragment }> }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => data?.preset && <PresetRunPageDialog preset={data.preset} />}
      </Await>
    </React.Suspense>
  );
};

const PresetRunPageDialog = (props: { preset: CustomPresetFieldsFragment }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [attachments, setAttachments] = React.useState<File[]>([]);

  const createWorkspace = async () => {
    setLoading(true);

    try {
      const res = await client.mutate<CreateCaraWorkspaceMutation>({
        mutation: CreateCaraWorkspaceDocument,
        variables: {
          input: {
            message: message.trim(),
            files: attachments,
            customPresetId: props.preset.id,
            documentIds: []
          }
        }
      });

      if (res.errors?.length) {
        throw new ApolloError({ graphQLErrors: res.errors });
      }

      const id = res.data?.createCaraWorkspace?.id;
      if (!id) {
        throw new Error('Failed to create workspace');
      }

      navigate(`/app/workspaces/${id}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="xl">
      <DialogTitle>{props.preset.title}</DialogTitle>
      <DialogDescription>{props.preset.description}</DialogDescription>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          createWorkspace();
        }}
      >
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Input Files</Label>
                <Description>
                  Files you'd like Cara to use as context to answer questions
                </Description>
                <FileInput multiple onChange={(files) => setAttachments(files)} />
              </Field>
              <Field>
                <Label>Message</Label>
                <Description>
                  Any additional instructions or context you'd like Cara to use to complete the
                  task.
                </Description>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.currentTarget.value)}
                  rows={5}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={loading} onClick={() => navigate(-1)}>
            Close
          </Button>
          <Button type="submit" color="sky" loading={loading}>
            Create Workspace
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};
